import { ChangeDetectionStrategy, Component, computed } from '@angular/core';

import { EHorizontalPosition, Widgets } from '@common/enums';
import { isWidgetSynchronized } from '@common/helpers/widget-envelope';

import { type TextEntity } from '@sites/entity-core';

import { BaseWidgetComponent } from '../base-widget.abstract';
import { CdkDrag, CdkDragPreview, CdkDragPlaceholder, CdkDragHandle } from '@angular/cdk/drag-drop';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { ViewResolverComponent } from '@web/layout/content/page-content/resolvers/view-resolver.component';
import { ItemControlsComponent } from '@web/layout/content/page-content/components/controls/item-controls/item-controls.component';
import { DragHandleComponent } from '@web/layout/content/page-content/components/drag-handle/drag-handle.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'web-widget',
    templateUrl: './default-widget.component.html',
    styleUrls: ['./default-widget.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CdkDrag,
        TooltipModule,
        CdkDragPreview,
        CdkDragPlaceholder,
        ViewResolverComponent,
        ItemControlsComponent,
        DragHandleComponent,
        CdkDragHandle,
        TranslateModule,
    ],
})
export class DefaultWidgetComponent extends BaseWidgetComponent {
    protected readonly Widgets = Widgets;

    public readonly widgetData = computed(() => this.widget().data as any);
    public readonly position = computed(() => this.widgetData()?.position);

    public readonly isWidgetText = computed<boolean>(() => this.widgetName() === Widgets.TEXT);

    public readonly isSynchronized = computed<boolean>(() => isWidgetSynchronized(this.widget()));

    private readonly useTabletPosition = computed<boolean>(() => this.isTablet() && !this.isSynchronized());
    private readonly useMobilePosition = computed<boolean>(() => this.isMobile() && !this.isSynchronized());

    private readonly x = computed<number>(() => {
        const position = this.position();

        if (this.useMobilePosition()) {
            return position?.mobileX ?? 0;
        }

        if (this.useTabletPosition()) {
            return position?.tabletX ?? position?.x ?? 0;
        }

        return position?.x ?? 0;
    });

    public readonly positionX = computed<string>(() => {
        const xValue = this.x();
        if (!this.isWidgetText()) {
            return `${xValue}px`;
        }

        const position = this.position();
        const currentWidth = this.useTabletPosition()
            ? (position?.tabletWidth ?? position?.width ?? 100)
            : (position?.width ?? 100);

        if (currentWidth <= 100) {
            return `${xValue}px`;
        }

        const horizontalAlign = (this.widget().data as TextEntity['data'])?.horizontalAlign;
        if (horizontalAlign === EHorizontalPosition.L) {
            return `calc(${xValue}px)`;
        }

        if (horizontalAlign === EHorizontalPosition.R) {
            const diff = currentWidth - 100;
            return `calc(${xValue}px - ${diff}%)`;
        }

        const diff = (currentWidth - 100) / 2;
        return `calc(${xValue}px - ${diff}%)`;
    });

    public readonly positionY = computed<number>(() => {
        const position = this.position();

        if (this.useMobilePosition()) {
            return position?.mobileY ?? 0;
        }

        if (this.useTabletPosition()) {
            return position?.tabletY ?? position?.y ?? 0;
        }

        return position?.y ?? 0;
    });

    public readonly width = computed<number | null>(() => {
        if (!this.isWidgetText()) {
            return null;
        }

        const position = this.position();
        let widthValue = position?.width ?? 100;

        if (this.useTabletPosition() && position?.tabletWidth) {
            widthValue = position.tabletWidth;
        } else if (this.useMobilePosition() && position?.mobileWidth) {
            widthValue = position.mobileWidth;
        }

        if (widthValue > 100) {
            return widthValue;
        }

        return 100;
    });

    public readonly editControlZIndex = computed(() => this.position()?.zIndex + 1);
}
